import React from 'react';
import { Card, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';

// Shared phase-card chrome: a brand-headed card with a white body and an
// optional grey footer action band. Extracted from the project phase card so the
// in-order phases (OrderSections) wear the same UI — one shell, page-specific
// bodies. The project phase card is the fast-follow migration onto this shell.
//
// Layout: Card (zeroed padding) → header band (brand) [title · status · actions]
// → body (white) [children] → optional footer band (grey) [footer].

interface PhaseCardProps {
  /** Phase label, e.g. "Initial drop" / "Swap 1". Grows to fill the header. */
  title: React.ReactNode;
  /** Status indicator (a Badge) pinned to the header's right, before actions. */
  status?: React.ReactNode;
  /** Header action affordances (kebab menu, etc.), flush right on the brand band. */
  headerActions?: React.ReactNode;
  /** White card body. */
  children: React.ReactNode;
  /** Optional grey footer band for primary actions (start/stop/bill). */
  footer?: React.ReactNode;
  /** Visually mark the card as selected (e.g. batch-billing pick). */
  selected?: boolean;
  className?: string;
}

const useStyles = makeStyles({
  // Zero the Card's own padding/gap so the bands can carry full-bleed
  // backgrounds. No overflow:hidden — it would clip a body picker's dropdown.
  card: {
    ...shorthands.padding(0),
    ...shorthands.gap(0),
    '&[data-fui-focus-within]:focus-within::after': {
      ...shorthands.borderColor('transparent'),
    },
  },
  selected: {
    ...shorthands.outline('2px', 'solid', tokens.colorBrandStroke1),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    ...shorthands.padding('10px', '16px'),
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground,
  },
  title: {
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold,
    flexGrow: 1,
    minWidth: 0,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '4px',
    flexShrink: 0,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
    flexGrow: 1,
    ...shorthands.padding('12px', '16px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    rowGap: '8px',
    flexWrap: 'wrap',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

const PhaseCard: React.FC<PhaseCardProps> = ({
  title,
  status,
  headerActions,
  children,
  footer,
  selected,
  className,
}) => {
  const styles = useStyles();
  return (
    <Card className={mergeClasses(styles.card, selected && styles.selected, className)}>
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
        {status}
        {headerActions && <div className={styles.headerActions}>{headerActions}</div>}
      </div>
      <div className={styles.body}>{children}</div>
      {footer && <div className={styles.footer}>{footer}</div>}
    </Card>
  );
};

export default PhaseCard;
